'use client'
import { useMemoizedContentGetter } from 'utils/contentful'
import { Typography, MediaImageStandard, LinkButton } from 'components/Contentful'
import Section from 'components/Section'
import { Grid } from '@achieve/ascend'
import { Button } from '@/components/Button'
import { useState } from 'react'
import { QuizFlow } from 'components/QuizFlow'
import classNames from 'classnames'

const PRODUCT_QUIZ_TYPE = 'product_quiz'
const VALUE_PROPS_TYPE = 'value_props'

function CloserStatement({ content }) {
  const { title, subTitle, image, button, altCta, config, valueProps } = useMemoizedContentGetter(
    content,
    ['title', 'subTitle', 'image', 'button', 'altCta', 'config', 'valueProps']
  )
  const [showQuizState, setShowQuizState] = useState(false)
  const variation = config?.jsonContent?.variation || 'default'
  const showPattern = config?.jsonContent?.showPattern || false

  return (
    <div className="relative">
      <div className="absolute inset-y-0 left-[calc((50%-50vw)-1%)] w-screen bg-[linear-gradient(178.86deg,#3C14FF_-8.78%,#1DB6FF_134.86%)] -z-10"></div>
      <Section
        className={
          variation === PRODUCT_QUIZ_TYPE
            ? 'text-white text-center max-h-[520px] flex overflow-hidden xl:text-left xl:h-[339px]'
            : `${
                variation === VALUE_PROPS_TYPE
                  ? 'bg-[linear-gradient(178.86deg,#3C14FF_-8.78%,#1DB6FF_134.86%)] max-h-[620px] lg:max-h-[520px]'
                  : 'bg-[#30f]'
              } text-white text-center max-h-[520px] flex overflow-hidden xl:text-left xl:min-h-[339px]`
        }
        childContainerClassName={variation === PRODUCT_QUIZ_TYPE ? 'lg:px-[114px]!' : ''}
        data-testid="closer-statement-section"
        showPattern={
          showPattern && (variation === PRODUCT_QUIZ_TYPE || variation === VALUE_PROPS_TYPE)
        }
      >
        <Grid
          container
          alignItems="center"
          className={
            variation === PRODUCT_QUIZ_TYPE
              ? 'pt-10 xl:pt-0 bg-[linear-gradient(178.86deg,#3C14FF_-8.78%,#1DB6FF_134.86%)] rounded-xl max-h-full flex justify-between'
              : 'pt-8 xl:max-w-[920px] xl:min-h-[339px] xl:m-auto xl:pt-0'
          }
          data-testid="closer-statement-outer-grid-container"
        >
          <Grid
            size={{ xs: 12, xl: variation === VALUE_PROPS_TYPE ? 12 : 6 }}
            sx={{ minHeight: '100%' }}
            className={classNames(
              variation === PRODUCT_QUIZ_TYPE
                ? 'pt-6 lg:pt-0 pb-[32px] mb-[-30px] h-full order-1 px-6 lg:px-0 lg:pl-[110px]'
                : 'order-1 ',
              variation === VALUE_PROPS_TYPE ? 'block' : 'flex'
            )}
            data-testid="closer-statement-grid-item-text"
          >
            <div
              className={classNames(
                variation !== VALUE_PROPS_TYPE && 'xl:max-w-[450px] xl:p-0 m-auto',
                variation === VALUE_PROPS_TYPE && 'py-10 lg:py-0'
              )}
              data-testid="closer-statement-text-container"
            >
              <Typography
                content={title?.textContent}
                variant="displayLg"
                fontWeight="bold"
                className={classNames(
                  variation === PRODUCT_QUIZ_TYPE
                    ? 'pb-6 xl:pb-[36px] lg:text-[40px]'
                    : 'leading-[140%] pb-6 xl:pb-8',
                  variation === VALUE_PROPS_TYPE && 'text-left lg:text-center text-[48px] lg:pt-8'
                )}
              />
              {subTitle?.textContent && (
                <Typography
                  content={subTitle?.textContent}
                  variant="labelSm"
                  fontWeight="regular"
                  className={classNames(
                    variation === VALUE_PROPS_TYPE
                      ? 'pb-6 xl:pb-8 text-[16px] lg:text-[18px] text-left lg:text-center max-w-[800px] m-auto leading-[1.7] '
                      : 'pb-6 xl:pb-8 lg:text-[18px] w-full leading-[1.7]',
                    variation === PRODUCT_QUIZ_TYPE && 'mt-[-13px] leading-[1.7]'
                  )}
                />
              )}
              <div
                className={classNames(
                  '[&_.button]:z-1 [&_.button]:mx-[2.4px] [&_.button]:my-0 [&_.button]:px-6 [&_.button]:py-3 xl:flex xl:m-0.5 xl:[&_.button]:py-2 xl:[&_.button]:px-5',
                  variation === VALUE_PROPS_TYPE && '[&_a]:m-auto'
                )}
              >
                {variation === PRODUCT_QUIZ_TYPE && (
                  <>
                    <Button
                      className="cursor-pointer px-[24px]"
                      variant="contained"
                      color="secondary"
                      data-testid="tcb-ok-button"
                      onClick={() => setShowQuizState(true)}
                    >
                      <Typography
                        content={'Take the short quiz'}
                        variant="labelLg"
                        fontWeight="bold"
                        className=""
                      />
                    </Button>
                    {showQuizState && (
                      <QuizFlow
                        associatedProduct={
                          config?.jsonContent?.associatedProduct || 'Personal Loans'
                        }
                        onCLose={() => setShowQuizState(false)}
                      />
                    )}
                  </>
                )}
                {button && (
                  <LinkButton
                    className={classNames(
                      'button',
                      variation === VALUE_PROPS_TYPE && 'w-full md:w-auto'
                    )}
                    content={button}
                    typographicOptions={{
                      className:
                        variation === VALUE_PROPS_TYPE
                          ? 'bodyLg font-bold!'
                          : 'bodySm lg:bodyLg font-bold!',
                      'data-testid': 'closer-statement-button-text',
                    }}
                    color="secondary"
                    data-testid="closer-statment-button"
                    track={{
                      ...{
                        click_id: button?.linkText?.json?.content?.[0]?.content[0]?.value,
                        list_name: 'Product Page Closer',
                        nav_link_section: 'Closer Statement',
                        track_event: 'internal_campaign_click',
                        click_type: 'Button Click',
                        event_action: 'button_click',
                        event_type: 'button_click',
                      },
                      ...button.event,
                    }}
                  />
                )}

                {Object.hasOwnProperty.call(altCta?.json || {}, 'textContent') && (
                  <Typography
                    content={altCta?.textContent}
                    variant="bodySm"
                    fontWeight="bold"
                    className={
                      // styles['alt-cta']
                      'self-center pt-10 xl:pl-10 xl:pr-0 xl:py-0'
                    }
                    data-testid="closer-statement-text-area-alt-cta"
                  />
                )}
              </div>
              {variation === VALUE_PROPS_TYPE && (
                <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-start lg:items-center justify-center gap-3 md:gap-12 lg:pt-8 pt-6 pb-8">
                  {valueProps?.gridItemsCollection?.items?.map((item) => (
                    <div
                      key={item?.sys?.id}
                      className="flex items-center gap-2 text-white font-medium opacity-90 hover:opacity-100 transition-opacity"
                    >
                      {/* {item.icon} */}
                      <MediaImageStandard
                        className="w-4 min-w-4!"
                        content={item?.mediaContent}
                        alt={item?.mediaAltText}
                        height={16}
                        width={16}
                      />
                      <span className="text-sm md:text-base whitespace-nowrap">
                        {item?.mediaAltText}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Grid>
          <Grid
            size={{ xs: 12, xl: 5.5 }}
            className={`order-2 w-[inherit] h-[inherit] flex justify-center items-end xl:max-w-[45%] ${
              variation === PRODUCT_QUIZ_TYPE ? 'xl:pt-6' : 'pt-6'
            }`}
            data-testid="closer-statement-grid-item-image"
          >
            <MediaImageStandard
              objectFit="contain"
              objectPosition="center top"
              content={image?.mediaContent}
              alt={image?.mediaAltText}
              data-testid="closer-statement-image"
              placeholder={'empty'}
              className={'object-contain object-[center_bottom] max-h-[250px] xl:max-h-[stretch]'}
            />
          </Grid>
        </Grid>
      </Section>
    </div>
  )
}
export { CloserStatement }
export default CloserStatement
